<template>
  <avs-card class="pb-4">
    <!-- ===== HEADER ===== -->
    <template #title>
      <v-toolbar class="mt-4 custom-toolbar bgTable" elevation="0">
        <!-- RECHERCHE (BACKEND) -->
        <v-text-field
          class="mr-4"
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
          @input="onSearchChange"
          @click:clear="onSearchClear"
        />
        <!-- FILTRE STATUT -->
        <v-select
          class="mr-4"
          dense
          hide-details
          :label="$t('Statut')"
          v-model="statusFilter"
          :items="statusItems"
          item-text="label"
          item-value="value"
        />
        <!-- DATE DÉBUT -->
        <v-menu v-model="startMenu" transition="scale-transition" offset-y min-width="auto">
          <template #activator="{ on, attrs }">
            <v-text-field
              v-bind="attrs"
              v-on="on"
              dense
              hide-details
              readonly
              class="mr-4"
              :label="$t('Date début')"
              :value="startDate"
              clearable
              @click:clear="startDate=null"
            />
          </template>
          <v-date-picker v-model="startDate" @input="startMenu = false" />
        </v-menu>
        <!-- DATE FIN -->
        <v-menu v-model="endMenu" transition="scale-transition" offset-y min-width="auto">
          <template #activator="{ on, attrs }">
            <v-text-field
              v-bind="attrs"
              v-on="on"
              dense
              hide-details
              readonly
              class="mr-4"
              :label="$t('Date fin')"
              :value="endDate"
              clearable
              @click:clear="endDate=null"
            />
          </template>
          <v-date-picker v-model="endDate" @input="endMenu = false" />
        </v-menu>
        <v-spacer />
        <!-- BOUTON EXPORT EXCEL -->
        <v-btn small outlined color="success" class="mr-2" :loading="isExporting" @click="exportToExcel">
          <v-icon left>mdi-microsoft-excel</v-icon>{{ $t('Export Excel') }}
        </v-btn>
        <!-- BOUTON REFRESH -->
        <v-btn small outlined color="primary" @click="fetchData(true)">
          <v-icon left>mdi-refresh</v-icon>{{ $t('Actualiser') }}
        </v-btn>
      </v-toolbar>
    </template>
    <!-- ===== CONTENT ===== -->
    <template #content>
      <!-- KPIs Demandes de rédemption -->
      <v-row class="stats-section ma-4" v-if="redemptionStats">
        <v-col cols="12">
          <div class="stats-container">
            <!-- Total demandes -->
            <v-card class="stat-card stat-card-main" elevation="4">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label">{{ $t('Total demandes') }}</div>
                  <div class="stat-value">{{ formatNumber(redemptionStats.total) }}</div>
                </div>
                <div class="stat-icon">
                  <v-icon size="40" color="white">mdi-hand-coin</v-icon>
                </div>
              </div>
              <v-divider class="my-0" style="border-color: rgba(255,255,255,0.3);" />
              <div class="stat-secondary mt-2">
                <v-icon small color="white">mdi-cash</v-icon>
                {{ formatNumber(redemptionStats.fulfilled_amount) }} XOF {{ $t('encaissé') }}
              </div>
            </v-card>

            <!-- En attente -->
            <v-card class="stat-card stat-card-product bgTable" elevation="3">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label">{{ $t('En attente') }}</div>
                  <div class="stat-value stat-value-warning">{{ formatNumber(redemptionStats.pending) }}</div>
                </div>
                <div class="stat-icon stat-icon-warning">
                  <v-icon size="40" color="orange">mdi-clock-alert</v-icon>
                </div>
              </div>
            </v-card>

            <!-- Approuvées -->
            <v-card class="stat-card stat-card-product bgTable" elevation="3">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label">{{ $t('Approuvées') }}</div>
                  <div class="stat-value" style="color: #1976d2;">{{ formatNumber(redemptionStats.approved) }}</div>
                </div>
                <div class="stat-icon" style="background: rgba(25, 118, 210, 0.12);">
                  <v-icon size="40" color="primary">mdi-check-circle</v-icon>
                </div>
              </div>
              <div class="stat-footer mt-1">
                <v-icon x-small>mdi-cash-check</v-icon>
                {{ formatNumber(redemptionStats.approved_amount) }} XOF {{ $t('à encaisser') }}
              </div>
            </v-card>

            <!-- Honorées -->
            <v-card class="stat-card stat-card-product bgTable" elevation="3">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label">{{ $t('Honorées') }}</div>
                  <div class="stat-value stat-value-success">{{ formatNumber(redemptionStats.fulfilled + redemptionStats.paid) }}</div>
                </div>
                <div class="stat-icon stat-icon-success">
                  <v-icon size="40" color="success">mdi-check-decagram</v-icon>
                </div>
              </div>
            </v-card>

            <!-- Rejetées -->
            <v-card class="stat-card stat-card-product bgTable" elevation="3">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="stat-label">{{ $t('Rejetées') }}</div>
                  <div class="stat-value stat-value-error">{{ formatNumber(redemptionStats.rejected) }}</div>
                </div>
                <div class="stat-icon stat-icon-error">
                  <v-icon size="40" color="error">mdi-close-circle</v-icon>
                </div>
              </div>
            </v-card>
          </div>
        </v-col>
      </v-row>

      <v-data-table
        class="ma-6 bgTable"
        dense
        :headers="headers"
        :items="items"
        item-key="id"
        :no-data-text="$t('messages.information.no_data')"
        hide-default-footer
        :loading="initialLoading"
        disable-pagination
        :items-per-page="-1"
      >
        <template #item.user="{ item }">
          <div class="text-no-wrap font-weight-medium">
            {{ userLabel(item) }}
          </div>
          <div class="text-caption grey--text">
            {{ item.user_phone_number || item.user_email || '' }}
          </div>
        </template>
        <template #item.vehicle_type="{ item }">
          <span>{{ item.vehicle_type || '-' }}</span>
        </template>
        <template #item.origin_station="{ item }">
          <span>{{ item.origin_station || '-' }}</span>
        </template>
        <template #item.request_type="{ item }">
          <avs-chip :label="true">
            {{ item.request_type === 'fuel' ? $t('Carburant') : $t('Espèces') }}
          </avs-chip>
        </template>
        <template #item.points_requested="{ item }">
          {{ formatNumber(item.points_requested) }}
        </template>
        <template #item.amount_or_liters="{ item }">
          <span v-if="item.request_type === 'cash'">
            {{ formatNumber(item.payout_amount) }} FCFA
          </span>
          <span v-else>
            {{ formatNumber(item.liters_authorized) }} L
          </span>
        </template>
        <template #item.payout_amount="{ item }">
          {{ formatNumber(item.payout_amount) }} FCFA
        </template>
        <template #item.created_at="{ item }">
          {{ toDatetime(item.created_at) }}
        </template>
        <template #item.status="{ item }">
          <component :is="statusComponent(item.status)" />
        </template>
        <template #item.actions="{ item }">
          <div class="d-flex align-center justify-center">
            <v-chip
              v-if="item.replaced_by_next"
              x-small
              color="green lighten-4"
              text-color="green darken-3"
              class="mr-2"
            >
              <v-icon x-small left>mdi-check-circle</v-icon>
              Traitee
            </v-chip>
            <avs-button
              icon
              small
              color="primary"
              :iconI="'mdi-finance'"
              label
              fab
              @click.stop="goToUserTransactions(item)"
              :spantext="$t('Transactions')"
            />
            <avs-button
              icon
              small
              color="secondary"
              :iconI="'mdi-eye'"
              label
              fab
              @click.stop="openDialog(item)"
              :spantext="$t('Detail')"
            />
            <avs-button
              v-if="canApprove(item)"
              icon
              small
              color="success"
              :iconI="'mdi-check-decagram'"
              label
              fab
              @click.stop="askConfirm('approve', item)"
              :spantext="$t('commons.accept')"
            />
            <avs-button
              v-if="canReject(item)"
              icon
              small
              color="error"
              :iconI="'mdi-close-circle'"
              label
              fab
              @click.stop="askConfirm('reject', item)"
              :spantext="$t('commons.reject')"
            />
            <avs-button
              v-if="canMarkPaid(item)"
              icon
              small
              color="teal"
              :iconI="'mdi-cash-check'"
              label
              fab
              @click.stop="askConfirm('paid', item)"
              :spantext="$t('Marquer payé')"
            />
          </div>
        </template>
      </v-data-table>
      
      <!-- SENTINEL POUR SCROLL INFINI -->
      <div ref="infiniteScrollSentinel" class="text-center py-4" style="min-height: 60px;">
        <v-progress-circular v-if="loadingMore" indeterminate size="24" />
        <div v-else-if="!hasMore && items.length">
          <small class="grey--text">{{ $t("Fin de la liste") }}</small>
        </div>
        <div v-else-if="hasMore && items.length && !loadingMore">
          <v-btn small text color="primary" @click="loadMore">
            <v-icon left small>mdi-chevron-down</v-icon>
            {{ $t("Charger plus") }}
          </v-btn>
        </div>
      </div>
      
      <!-- DIALOG DÉTAIL -->
      <v-dialog v-model="dialog.open" width="600px">
        <RedemptionReviewDialog
          v-model="dialog.item"
          @cancel="dialog.open=false"
          @approved="afterAction"
          @rejected="afterAction"
          @paid="afterAction"
        />
      </v-dialog>
      
      <!-- DIALOG CONFIRMATION -->
      <v-dialog v-model="confirm.open" max-width="720" persistent>
        <v-card v-if="confirm.open" class="rounded-xl overflow-hidden">
          <!-- Header colore -->
          <v-sheet :color="confirmMeta.color" dark class="pa-5">
            <div class="d-flex align-center">
              <v-avatar size="48" class="mr-4" :color="confirmMeta.color + ' darken-1'">
                <v-icon large>{{ confirmMeta.icon }}</v-icon>
              </v-avatar>
              <div>
                <div class="text-h6 font-weight-bold">{{ confirmMeta.title }}</div>
                <div class="text-body-2 opacity-80">{{ confirmMeta.subtitle }}</div>
              </div>
            </div>
          </v-sheet>

          <v-card-text class="pt-6 pb-2">
            <v-row>
              <!-- Carte Demandeur -->
              <v-col cols="12" md="6">
                <v-card outlined class="rounded-lg">
                  <v-card-text class="pa-4">
                    <div class="text-overline grey--text mb-3">
                      <v-icon small class="mr-1">mdi-account-circle</v-icon>{{ $t('Demandeur') }}
                    </div>
                    <div class="d-flex align-center mb-4">
                      <v-avatar size="44" color="primary" class="mr-3">
                        <span class="white--text font-weight-bold">
                          {{ (userLabel(confirm.item) || '?').charAt(0) }}
                        </span>
                      </v-avatar>
                      <div>
                        <div class="font-weight-bold text-subtitle-1">{{ userLabel(confirm.item) }}</div>
                        <div class="text-caption grey--text">
                          <v-icon x-small class="mr-1">mdi-phone</v-icon>{{ confirm.item?.user_phone_number || '-' }}
                        </div>
                      </div>
                    </div>
                    <v-divider class="mb-3" />
                    <div class="d-flex justify-space-between text-caption mb-2">
                      <span class="grey--text">{{ $t('Email') }}</span>
                      <span>{{ confirm.item?.user_email || '-' }}</span>
                    </div>
                    <div class="d-flex justify-space-between text-caption mb-2">
                      <span class="grey--text">{{ $t('Entreprise') }}</span>
                      <span>{{ confirm.item?.company_name || '-' }}</span>
                    </div>
                    <div class="d-flex justify-space-between text-caption align-center">
                      <span class="grey--text">{{ $t('Type') }}</span>
                      <v-chip x-small :color="confirm.item?.request_type === 'fuel' ? 'blue lighten-4' : 'orange lighten-4'" label>
                        <v-icon x-small left>{{ confirm.item?.request_type === 'fuel' ? 'mdi-gas-station' : 'mdi-cash' }}</v-icon>
                        {{ confirm.item?.request_type === 'fuel' ? $t('Carburant') : $t('Espèces') }}
                      </v-chip>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Carte Montants -->
              <v-col cols="12" md="6">
                <v-card outlined class="rounded-lg">
                  <v-card-text class="pa-4">
                    <div class="text-overline grey--text mb-3">
                      <v-icon small class="mr-1">mdi-coin</v-icon>{{ $t('Montants') }}
                    </div>
                    <div class="text-center mb-4">
                      <div class="text-h4 font-weight-bold primary--text">
                        {{ formatNumber(confirm.item?.points_requested) }}
                      </div>
                      <div class="text-caption grey--text">{{ $t('Points demandés') }}</div>
                    </div>
                    <v-divider class="mb-3" />
                    <div v-if="confirm.item?.request_type === 'cash'" class="d-flex justify-space-between align-center">
                      <span class="grey--text text-caption">{{ $t('Montant estimé') }}</span>
                      <span class="text-h6 font-weight-bold success--text">{{ formatNumber(confirm.item?.payout_amount) }} <small>XOF</small></span>
                    </div>
                    <div v-else class="d-flex justify-space-between align-center">
                      <span class="grey--text text-caption">{{ $t('Litres autorisés') }}</span>
                      <span class="text-h6 font-weight-bold info--text">{{ confirm.item?.liters_authorized || '-' }} <small>L</small></span>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Statut actuel -->
            <div class="d-flex align-center mt-4 pa-2 grey lighten-4 rounded">
              <span class="text-caption grey--text mr-2">{{ $t('Statut actuel') }} :</span>
              <component :is="statusComponent(confirm.item?.status)" />
            </div>

            <!-- Raison du refus -->
            <v-expand-transition>
              <div v-if="confirmMeta.askReason" class="mt-5">
                <v-textarea
                  v-model="confirm.reason"
                  :label="$t('Raison du refus') + ' *'"
                  prepend-inner-icon="mdi-message-text"
                  auto-grow
                  rows="3"
                  outlined
                  dense
                  :rules="[v => !!(v && v.trim()) || $t('Champ obligatoire')]"
                />
              </div>
            </v-expand-transition>

            <!-- Reference de paiement -->
            <v-expand-transition>
              <div v-if="confirmMeta.askReference" class="mt-5">
                <v-text-field
                  v-model="confirm.reference"
                  :label="$t('Référence de paiement (optionnel)')"
                  prepend-inner-icon="mdi-bank-transfer"
                  outlined
                  dense
                />
              </div>
            </v-expand-transition>
          </v-card-text>

          <v-divider />
          <v-card-actions class="pa-4 d-flex justify-end">
            <v-btn text class="mr-2" @click="confirm.open = false">
              <v-icon left small>mdi-close</v-icon>{{ $t('commons.cancel') }}
            </v-btn>
            <v-btn
              :color="confirmMeta.color"
              dark
              depressed
              :loading="confirm.loading"
              @click="doConfirm"
              class="px-6"
            >
              <v-icon left>{{ confirmMeta.icon }}</v-icon>{{ confirmMeta.cta }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import RedemptionAdminService from "@/services/RedemptionAdminService";
import { saveAs } from "file-saver";
import { toDatetime } from "@/utils/datetime";
import { Approved, Canceled, Pending, Paid, Fulfilled, Overdue, Rejected } from "@/components/generics/status";
import RedemptionReviewDialog from "@/components/activity/RedemptionReviewDialog.vue";

function debounce(fn, wait = 400) {
  let t = null;
  return function(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

export default {
  name: "RedemptionsAdmin",
  components: { Approved, Canceled, Pending, Paid, Fulfilled, RedemptionReviewDialog, Overdue, Rejected  },
  data() {
    return {
      search: "",
      companyFilter: null,
      items: [],
      count: 0,
      redemptionStats: null,
      headers: [
        { text: this.$t("Utilisateur"), value: "user" },
        { text: this.$t("Type vehicule"), value: "vehicle_type" },
        { text: this.$t("Station d'origine"), value: "origin_station" },
        { text: this.$t("Type"), value: "request_type" },
        { text: this.$t("Points"), value: "points_requested" },
        { text: this.$t("Litres"), value: "amount_or_liters" },
        { text: this.$t("Montant (XOF)"), value: "payout_amount" },
        { text: this.$t("Statut"), value: "status" },
        { text: this.$t("commons.created_at"), value: "created_at" },
        { text: this.$t("commons.action"), value: "actions", sortable: false, align: "center" },
      ],
      dialog: { open: false, item: null },
      // Filtres
      statusFilter: "all",
      startDate: null,
      endDate: null,
      startMenu: false,
      endMenu: false,
      // Scroll infini (DRF)
      page: 1,
      pageSize: 20,
      hasMore: true,
      initialLoading: false,
      loadingMore: false,
      isExporting: false,
      observer: null,
      // anti-race (si l'utilisateur change filtres vite)
      requestKey: 0,
      // Modale confirmation
      confirm: {
        open: false,
        action: null,
        item: null,
        reason: "",
        reference: "",
        loading: false,
      },
    };
  },
  computed: {
    statusItems() {
      return [
        { value: "all",       label: this.$t("commons.all_simple") },
        { value: "pending",   label: this.$t("En attente") },
        { value: "approved",  label: this.$t("Approuvée") },
        { value: "rejected",  label: this.$t("Refusée") },
        { value: "paid",      label: this.$t("Payée") },
        { value: "fulfilled", label: this.$t("Honorée") },
        { value: "cancelled", label: this.$t("Annulée") },
        { value: "expired",   label: this.$t("Expirée") },
      ];
    },
    confirmMeta() {
      switch (this.confirm.action) {
        case "approve":
          return {
            color: "success",
            icon: "mdi-check-decagram",
            title: this.$t("Approuver la demande ?"),
            subtitle: this.$t("Les points seront réservés et, si c'est du carburant, un bon sera généré."),
            cta: this.$t("Approuver"),
            askReason: false,
            askReference: false,
          };
        case "reject":
          return {
            color: "error",
            icon: "mdi-close-circle",
            title: this.$t("Rejeter la demande ?"),
            subtitle: this.$t("Vous devez indiquer une raison de refus."),
            cta: this.$t("Rejeter"),
            askReason: true,
            askReference: false,
          };
        case "paid":
          return {
            color: "teal",
            icon: "mdi-cash-check",
            title: this.$t("Marquer comme payée ?"),
            subtitle: this.$t("Confirmez le paiement de la demande en espèces."),
            cta: this.$t("Valider le paiement"),
            askReason: false,
            askReference: true,
          };
        default:
          return {
            color: "primary",
            icon: "mdi-help-circle",
            title: "",
            subtitle: "",
            cta: this.$t("commons.confirm"),
            askReason: false,
            askReference: false,
          };
      }
    },
  },
  watch: {
    statusFilter() { this.fetchData(true); this.fetchRedemptionStats(); },
    startDate()    { this.fetchData(true); this.fetchRedemptionStats(); },
    endDate()      { this.fetchData(true); this.fetchRedemptionStats(); },
  },
  created() {
    this.onSearchChange = debounce(this.onSearchChange, 450);
    this.fetchData(true);
    this.fetchRedemptionStats();
  },
  mounted() {
    this.setupObserver();
  },
  beforeDestroy() {
    this.teardownObserver();
  },
  methods: {
    toDatetime,
    formatNumber(v) {
      let n = parseFloat(v) || 0;
      if (n === Math.floor(n)) n = Math.floor(n);
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    onSearchClear() {
      this.search = "";
      this.fetchData(true);
      this.fetchRedemptionStats();
    },
    onSearchChange() {
      // recherche backend => reset + reload
      this.fetchData(true);
      this.fetchRedemptionStats();
    },
    setupObserver() {
      this.teardownObserver();
      if (!("IntersectionObserver" in window)) return;
      
      this.observer = new IntersectionObserver(
        entries => {
          const [entry] = entries;
          if (entry && entry.isIntersecting && !this.loadingMore && this.hasMore) {
            this.loadMore();
          }
        },
        { root: null, rootMargin: "200px", threshold: 0.1 }
      );
      
      this.$nextTick(() => {
        if (this.$refs.infiniteScrollSentinel) {
          this.observer.observe(this.$refs.infiniteScrollSentinel);
        }
      });
    },
    teardownObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
    userLabel(item) {
      const full = [item?.user_first_name, item?.user_last_name].filter(Boolean).join(" ").trim();
      return (full || item?.user_username || ("#" + item?.user)).toUpperCase();
    },
    statusComponent(status) {
      switch ((status || "").toLowerCase()) {
        case "pending":   return "Pending";
        case "rejected": return "Rejected";
        case "cancelled": return "Overdue";
        case "expired":   return "Canceled";
        case "approved":  return "Approved";
        case "paid":      return "Paid";
        case "fulfilled": return "Fulfilled";
        default:          return "Pending";
      }
    },
    canApprove(item){ return item.status === "pending"; },
    canReject(item){ return item.status === "pending"; },
    canMarkPaid(item){ return item.status === "approved" && item.request_type === "cash"; },
    openDialog(item){ this.dialog.item = item; this.dialog.open = true; },
    askConfirm(action, item) {
      this.confirm.open = true;
      this.confirm.action = action;
      this.confirm.item = item;
      this.confirm.loading = false;
      this.confirm.reason = "";
      this.confirm.reference = "";
    },
    loadMore() {
      if (!this.hasMore || this.loadingMore || this.initialLoading) return;
      this.fetchData(false);
    },
    async fetchData(reset = false) {
      if (reset) {
        this.page = 1;
        this.items = [];
        this.hasMore = true;
      }
      if (!this.hasMore) return;
      if (this.initialLoading || this.loadingMore) return;
      
      const isFirstPage = this.page === 1;
      if (isFirstPage) this.initialLoading = true;
      else this.loadingMore = true;
      
      const myKey = ++this.requestKey;
      
      try {
        const params = {
          page: this.page,
          page_size: this.pageSize,
        };
        
        // ✅ recherche backend
        const s = (this.search || "").trim();
        if (s) params.search = s;
        if (this.companyFilter) params.company = this.companyFilter;
        if (this.statusFilter && this.statusFilter !== "all") params.status = this.statusFilter;
        if (this.startDate) params.start_date = this.startDate;
        if (this.endDate)   params.end_date = this.endDate;
        
        const data = await RedemptionAdminService.list(params);
        
        // anti-race: on ignore si une requête plus récente a déjà été lancée
        if (myKey !== this.requestKey) return;
        
        const results = data?.results || [];
        const next = data?.next || null;
        this.count = data?.count ?? this.count;
        
        this.items = this.items.concat(results);
        this.hasMore = !!next;
        if (this.hasMore) this.page += 1;
        
      } catch (e) {
        this.$store.commit("app/message", {
          msg: this.$t("messages.error.operation_failed"),
          color: "error",
        });
      } finally {
        this.initialLoading = false;
        this.loadingMore = false;
      }
    },
    goToUserTransactions(item) {
      const phone = item.user_phone_number || null;
      if (!phone) {
        this.$store.commit("app/message", {
          msg: this.$t("Numéro de téléphone introuvable pour cet utilisateur."),
          color: "warning",
        });
        return;
      }
      this.$router.push({
        name: "FidelityDetail",
        query: { search: phone },
      });
    },
    async doConfirm() {
      if (!this.confirm.item) return;
      this.confirm.loading = true;
      try {
        if (this.confirm.action === "approve") {
          await RedemptionAdminService.approve(this.confirm.item.id);
        } else if (this.confirm.action === "reject") {
          const reason = (this.confirm.reason || "").trim();
          if (!reason) {
            this.$store.commit("app/message", {
              msg: this.$t("Veuillez saisir une raison."),
              color: "error",
            });
            return;
          }
          await RedemptionAdminService.reject(this.confirm.item.id, reason);
        } else if (this.confirm.action === "paid") {
          await RedemptionAdminService.markPaid(this.confirm.item.id, this.confirm.reference || "");
        }
        this.confirm.open = false;
        this.afterAction();
      } catch (e) {
        this.onErr(e);
      } finally {
        this.confirm.loading = false;
      }
    },
    async fetchRedemptionStats() {
      try {
        const params = {};
        const s = (this.search || "").trim();
        if (s) params.search = s;
        if (this.statusFilter && this.statusFilter !== "all") params.status = this.statusFilter;
        if (this.startDate) params.start_date = this.startDate;
        if (this.endDate) params.end_date = this.endDate;
        this.redemptionStats = await RedemptionAdminService.stats(params);
      } catch (e) {
        console.error("Erreur chargement stats redemptions", e);
      }
    },
    afterAction() {
      this.fetchData(true);
      this.fetchRedemptionStats();
      this.$store.commit("app/message", { msg: this.$t("messages.success.operation"), color: "success" });
    },
    onErr() {
      this.$store.commit("app/message", { msg: this.$t("messages.error.operation_failed"), color: "error" });
    },
    async exportToExcel() {
      this.isExporting = true;
      try {
        const params = {};
        const s = (this.search || "").trim();
        if (s) params.search = s;
        if (this.statusFilter && this.statusFilter !== "all") params.status = this.statusFilter;
        if (this.startDate) params.start_date = this.startDate;
        if (this.endDate) params.end_date = this.endDate;
        const blob = await RedemptionAdminService.exportExcel(params);
        saveAs(blob, `redemptions_${new Date().toISOString().slice(0, 10)}.xlsx`);
        this.$store.commit("app/message", { msg: this.$t("Telecharge avec succes"), color: "success" });
      } catch (e) {
        console.error("Erreur export Excel :", e);
        this.$store.commit("app/message", { msg: this.$t("messages.error.operation_failed"), color: "error" });
      } finally {
        this.isExporting = false;
      }
    },
  }
};
</script>

<style scoped>
.opacity-80 { opacity: .8; }
.min-width-120 { min-width: 120px; }

/* ===== Section stats KPI ===== */
.stats-section {
  margin-top: 10px;
  margin-bottom: 0;
  padding: 5px 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  border-radius: 16px;
}
.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  padding: 0 5px;
}
.stat-card-main {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  border-radius: 20px !important;
  padding: 16px 24px;
  color: white;
  box-shadow: 0 10px 30px rgba(25, 118, 210, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  position: relative;
  overflow: hidden;
}
.stat-card-main::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  pointer-events: none;
}
.stat-card-main:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 40px rgba(25, 118, 210, 0.4) !important;
}
.stat-card-main .stat-secondary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}
.stat-card-product {
  border-radius: 16px !important;
  padding: 16px 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid #e3f2fd;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
}
.stat-card-product:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12) !important;
  border-color: #90caf9;
}
.stat-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  margin-bottom: 6px;
}
.stat-value {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.2;
}
.stat-value-warning { color: #ff9800; }
.stat-value-success { color: #4caf50; }
.stat-value-error { color: #f44336; }
.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}
.stat-icon-warning { background: rgba(255, 152, 0, 0.12); }
.stat-icon-success { background: rgba(76, 175, 80, 0.12); }
.stat-icon-error { background: rgba(244, 67, 54, 0.12); }
.stat-footer {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding-top: 8px;
  border-top: 1px solid #e0e0e0;
}
@media (max-width: 960px) {
  .stats-container {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .stats-container {
    grid-template-columns: 1fr;
  }
  .stat-value {
    font-size: 24px;
  }
}
</style>